import { getPedidoById } from '../../../services/pedidos';
import { getEncargoById } from '../../../services/encargos';
import {
	DOCUMENTOS_ENCARGOS,
	DOCUMENTOS_PEDIDOS,
} from '../constants/documentTypes';

// Obtener mes en texto español
const getMesTexto = mesIndex => {
	const meses = [
		'enero',
		'febrero',
		'marzo',
		'abril',
		'mayo',
		'junio',
		'julio',
		'agosto',
		'septiembre',
		'octubre',
		'noviembre',
		'diciembre',
	];
	return meses[mesIndex];
};

// Obtener fecha actual formateada
export const getFechaActual = () => {
	const now = new Date();
	return {
		dia: now.getDate().toString(),
		mes: getMesTexto(now.getMonth()),
		year: now.getFullYear().toString().slice(-2), // Últimos 2 dígitos
	};
};

// Parsear JSON de forma segura
const parseJSON = value => {
	if (typeof value === 'string') {
		try {
			return JSON.parse(value);
		} catch {
			return {};
		}
	}
	return value || {};
};

// Cargar datos básicos del cliente
const cargarDatosCliente = cliente => {
	if (!cliente) return {};

	const otrosDatos = parseJSON(cliente.otros_datos_contacto);

	return {
		nombreCompletoCliente:
			`${cliente.nombre || ''} ${cliente.apellidos || ''}`.trim(),
		nombreCliente:
			`${cliente.nombre || ''} ${cliente.apellidos || ''}`.trim(),
		identificadorCliente: cliente.identificador || '',
		identificacionCliente: cliente.identificador || '',
		identidicadorCliente: cliente.identificador || '',
		telefonoCliente: cliente.telefono || '',
		emailCliente: otrosDatos?.email || '',
		ubicacionCliente: otrosDatos?.domicilio || '',
		domicilioCliente: otrosDatos?.domicilio || '',
		localidadCliente: otrosDatos?.localidad || '',
		municipioCliente: otrosDatos?.municipio || '',
		ciudadCliente: otrosDatos?.ciudad || '',
		nacionalidadCliente: cliente.nacionalidad || '',
		estadoCivil: cliente.estado_civil || '',
	};
};

// Cargar datos del inmueble desde encargo
const cargarDatosInmuebleEncargo = inmueble => {
	if (!inmueble) return {};

	const ubicacionObj = parseJSON(inmueble.ubicacion);
	const superficieObj = parseJSON(inmueble.superficie);
	const otrosObj = parseJSON(inmueble.otros);

	const superficie =
		typeof superficieObj === 'object'
			? superficieObj.construida ||
				superficieObj.util ||
				superficieObj.parcela
			: superficieObj;

	return {
		ubicacionInmueble: ubicacionObj?.direccion || '',
		localidadInmueble: inmueble.localidad || '',
		cpInmueble: inmueble.codigo_postal || '',
		catastroInmueble: inmueble.id_catastral || '',
		tipologiaInmueble: inmueble.tipo_inmueble || '',
		habitacionesInmueble: inmueble.habitaciones?.toString() || '',
		bathroomsInmueble: inmueble.bathrooms?.toString() || '',
		superficieInmueble: superficie?.toString() || '',
		cocinaInmueble: inmueble.cocina || '',
		yearInmueble: inmueble.fecha_construccion?.toString() || '',
		valoracionInmueble: inmueble.valoracion?.toString() || '',
		vpoInmueble: otrosObj?.vpo ? 'Sí' : 'No',
	};
};

// Cargar datos del inmueble desde pedido
const cargarDatosInmueblePedido = pedido => {
	if (!pedido) return {};

	const superficieObj = parseJSON(pedido.superficie);
	const superficie =
		typeof superficieObj === 'object'
			? superficieObj.construida ||
				superficieObj.util ||
				superficieObj.parcela
			: superficieObj;

	return {
		ubicacionInmueble: pedido.localidad || '',
		localidadInmueble: pedido.localidad || '',
		cpInmueble: pedido.codigo_postal || '',
		habitacionesInmueble: pedido.habitaciones?.toString() || '',
		bathroomsInmueble: pedido.bathrooms?.toString() || '',
		superficieInmueble: superficie?.toString() || '',
		cocinaInmueble: pedido.cocina || '',
		valoracionInmueble: pedido.precio?.toString() || '',
		precioVentaInmueble: pedido.precio?.toString() || '',
		descripcionInmueble: pedido.descripcion || '',
	};
};

// Cargar datos específicos para COMPROMISO_DE_VENTA
const cargarDatosCompromisoVenta = encargo => {
	const data = {};
	const meses = getMesTexto;

	if (encargo?.inmueble) {
		data.precioVentaInmueble =
			(
				encargo.costeVivienda || encargo.inmueble.valoracion
			)?.toString() || '';

		// Si el inmueble tiene un cliente (propietario), usar sus datos
		if (encargo.inmueble.cliente) {
			data.nombreCliente =
				`${encargo.inmueble.cliente.nombre || ''} ${encargo.inmueble.cliente.apellidos || ''}`.trim();
			data.identificadorCliente =
				encargo.inmueble.cliente.identificador || '';
		}
	}

	// Fechas de validez
	if (encargo?.fecha_vencimiento) {
		const fechaVencimiento = new Date(encargo.fecha_vencimiento);
		data.validezDiaEnd = fechaVencimiento.getDate().toString();
		data.validezMesEnd = meses(fechaVencimiento.getMonth());
		data.validezYearEnd = fechaVencimiento
			.getFullYear()
			.toString()
			.slice(-2);
	}

	const fechaActual = getFechaActual();
	data.validezDiaStart = fechaActual.dia;
	data.validezMesStart = fechaActual.mes;
	data.validezYearStart = fechaActual.year;
	data.enSuNombreSi = 'X';
	data.enSuNombreNo = '';

	return data;
};

// Cargar datos específicos para GASTOS_VENDEDOR
const cargarDatosGastosVendedor = encargo => {
	const data = {};

	if (encargo?.inmueble) {
		data.localidad = encargo.inmueble.localidad || '';

		// Si el inmueble tiene un cliente (propietario), usar sus datos
		if (encargo.inmueble.cliente) {
			data.nombreCliente =
				`${encargo.inmueble.cliente.nombre || ''} ${encargo.inmueble.cliente.apellidos || ''}`.trim();
			data.identificadorCliente =
				encargo.inmueble.cliente.identificador || '';
		}
	}

	if (encargo?.hipoteca) {
		data.hipotecaPendienteInmueble = encargo.hipoteca.toString();
	}

	if (encargo?.comision) {
		data.gastosInmobiliaria = encargo.comision.toString();
	}

	return data;
};

// Cargar datos específicos para RECISION_DE_CONTRATO
const cargarDatosRecisionContrato = encargo => {
	const data = {};

	// Datos del inmueble
	if (encargo?.inmueble) {
		const ubicacionObj = parseJSON(encargo.inmueble.ubicacion);
		data.ubicacionInmueble = ubicacionObj?.direccion || '';
		data.localidadInmueble = encargo.inmueble.localidad || '';
		data.ciudadInmueble = encargo.inmueble.localidad || '';
		data.localidad = encargo.inmueble.localidad || '';

		// Si el inmueble tiene un cliente (propietario), usar sus datos
		// Esto sobrescribe los datos del cliente del encargo si existe
		if (encargo.inmueble.cliente) {
			data.nombreCliente =
				`${encargo.inmueble.cliente.nombre || ''} ${encargo.inmueble.cliente.apellidos || ''}`.trim();
			data.identificadorCliente =
				encargo.inmueble.cliente.identificador || '';
		}
	}

	return data;
};

// Cargar datos específicos para CONTRATOINTERMEDACIONWOLFIN
const cargarDatosContratoIntermediacion = pedido => {
	const data = {};

	if (pedido) {
		data.localidadInmueble = pedido.localidad || '';
		data.cpCliente = pedido.codigo_postal || '';
	}

	data.c1enSuNombre = 'X';
	data.c2enSuNombre = '';

	return data;
};

// Cargar datos específicos para DECLARACION_DE_COMISION
const cargarDatosDeclaracionComision = pedido => {
	const data = {};

	if (pedido) {
		data.localidadInmueble = pedido.localidad || '';
		data.cpInmueble = pedido.codigo_postal || '';
	}

	data.enSuNombreSi = 'X';
	data.enSuNombreNo = '';

	return data;
};

// Cargar datos específicos para PROPUESTA_DE_COMPRAVENTA
const cargarDatosPropuestaCompraventa = pedido => {
	const data = {};

	if (pedido) {
		data.cpInmueble = pedido.codigo_postal || '';
		data.precioCompraventaPropuestoComprador =
			pedido.precio?.toString() || '';
	}

	data.enSuNombreSi = 'X';
	data.enSuNombreNo = '';

	return data;
};

// Cargar datos específicos para RECONOCIMIENTO_DE_HONORARIOS
const cargarDatosReconocimientoHonorarios = pedido => {
	const data = {};

	if (pedido) {
		data.precioVentaInmueble = pedido.precio?.toString() || '';
	}

	return data;
};

// Cargar datos específicos para ANEXO_COMPROMISO_DE_VENTA
const cargarDatosAnexoCompromisoVenta = encargo => {
	const data = {};

	if (encargo?.inmueble) {
		const ubicacionObj = parseJSON(encargo.inmueble.ubicacion);
		data.ubicacionInmueble = ubicacionObj?.direccion || '';
		data.localidadInmueble = encargo.inmueble.localidad || '';
		data.localidad = encargo.inmueble.localidad || '';

		// Si el inmueble tiene un cliente (propietario), usar sus datos
		if (encargo.inmueble.cliente) {
			data.nombreCliente =
				`${encargo.inmueble.cliente.nombre || ''} ${encargo.inmueble.cliente.apellidos || ''}`.trim();
			data.identificadorCliente =
				encargo.inmueble.cliente.identificador || '';
		}
	}

	data.enSuNombreSi = 'X';
	data.enSuNombreNo = '';

	return data;
};

// Función principal para cargar datos de encargos
export const cargarDatosEncargo = async (encargoUuid, tipoSeleccionado) => {
	try {
		const encargo = await getEncargoById(encargoUuid);
		const fechaActual = getFechaActual();

		let data = {
			...fechaActual,
			...cargarDatosCliente(encargo?.cliente),
			...cargarDatosInmuebleEncargo(encargo?.inmueble),
		};

		// Comisión del encargo
		if (encargo?.comision) {
			data.honorarios = encargo.comision.toString();
		}

		// Datos específicos según el tipo de documento
		switch (tipoSeleccionado) {
			case 'COMPROMISO_DE_VENTA':
				data = { ...data, ...cargarDatosCompromisoVenta(encargo) };
				break;
			case 'GASTOS_VENDEDOR':
				data = { ...data, ...cargarDatosGastosVendedor(encargo) };
				break;
			case 'RECISION_DE_CONTRATO':
				data = { ...data, ...cargarDatosRecisionContrato(encargo) };
				break;
			case 'ANEXO_COMPROMISO_DE_VENTA':
				data = { ...data, ...cargarDatosAnexoCompromisoVenta(encargo) };
				break;
			default:
				break;
		}

		return data;
	} catch (error) {
		console.error('Error cargando datos del encargo:', error);
		throw error;
	}
};

// Función principal para cargar datos de pedidos
export const cargarDatosPedido = async (pedidoUuid, tipoSeleccionado) => {
	try {
		const pedido = await getPedidoById(pedidoUuid);
		const fechaActual = getFechaActual();

		let data = {
			...fechaActual,
			...cargarDatosCliente(pedido?.cliente),
			...cargarDatosInmueblePedido(pedido),
		};

		// Datos específicos según el tipo de documento
		switch (tipoSeleccionado) {
			case 'CONTRATOINTERMEDACIONWOLFIN':
				data = {
					...data,
					...cargarDatosContratoIntermediacion(pedido),
				};
				break;
			case 'DECLARACION_DE_COMISION':
				data = { ...data, ...cargarDatosDeclaracionComision(pedido) };
				break;
			case 'PROPUESTA_DE_COMPRAVENTA':
				data = { ...data, ...cargarDatosPropuestaCompraventa(pedido) };
				break;
			case 'RECONOCIMIENTO_DE_HONORARIOS':
				data = {
					...data,
					...cargarDatosReconocimientoHonorarios(pedido),
				};
				break;
			default:
				break;
		}

		return data;
	} catch (error) {
		console.error('Error cargando datos del pedido:', error);
		throw error;
	}
};

// Determinar si un tipo de documento requiere autofill
export const requiereAutofill = (tipoSeleccionado, pedidoUuid, encargoUuid) => {
	if (!tipoSeleccionado) return false;

	const esDocumentoEncargo = DOCUMENTOS_ENCARGOS.some(
		doc => doc.value === tipoSeleccionado,
	);
	const esDocumentoPedido = DOCUMENTOS_PEDIDOS.some(
		doc => doc.value === tipoSeleccionado,
	);

	return (
		(esDocumentoEncargo && encargoUuid) || (esDocumentoPedido && pedidoUuid)
	);
};
